import React, { useEffect, useState } from 'react';
import '../Styles/IndividualRegistrationReview.scss';
import { RetreatPageOptions } from '../Constants/RegistrationPageOptions';
import {
   getRetreatConfig, getRoomSurcharge, getRetreatTotal, getPerSonFee, getAttendeeCount,
   getRegistrationFeeDue, getSonLines
} from '../Constants/RetreatConfig';
import DateHelpers from '../Helper/DateHelpers';

function RetreatRegistrationReview({ week, roomType, sonNames, sonCount = 0,
   craftFairTable, groupJoined, setGroupJoined, promoCode, signatureData, setSelectedPage,
   ProcessRegistration, AddToCart }) {

   const [addToCartError, setAddToCartError] = useState('')
   const [addingToCart, setAddingToCart] = useState(false)

   const [groupName, setGroupName] = useState('');
   const [campFee, setCampFee] = useState('');
   const [registrationFee, setRegistrationFee] = useState('');
   const [reviewPaymentDue, setReviewPaymentDue] = useState('');

   const config = getRetreatConfig(week);
   const roomSurcharge = getRoomSurcharge(week, roomType);
   const perSonFee = getPerSonFee(week);
   const sonLines = getSonLines(week, { sonNames });

   // Both fees are charged per person, so the father and every son he listed are counted into each
   // line rather than the sons arriving as a lump surcharge underneath them.
   const attendeeCount = getAttendeeCount(week, sonCount);
   const registrationFeeDue = getRegistrationFeeDue(week, sonCount);
   const campFeeDue = Number(week?.camp_fee ?? 0) * attendeeCount;

   useEffect(() => {
      if (groupJoined != null) {
         setGroupName(groupJoined.group_name);
      } else {
         setGroupJoined({ group_id: null });
         setGroupName('Attending as Individual');
      }

      setCampFee(parseFloat(week.camp_fee).toFixed(2));
      setRegistrationFee(parseFloat(week.registration_fee).toFixed(2));
      setReviewPaymentDue(getRetreatTotal(week, { roomType, sonCount }).toFixed(2));
   }, []);

   function handlePrevious() {
      setSelectedPage(RetreatPageOptions.Disclaimer);
   }

   async function handleNext() {
      setAddingToCart(true)
      setAddToCartError('')
      const message = await AddToCart()
      if (message) {
         setAddToCartError(message)
         setAddingToCart(false)
      }
   }

   return (
      <div className="IndividualRegistrationReviewContent">
         <div className="IndividualRegistrationReviewHeading">
            Review Registration Information
         </div>
         <div className="IndividualRegistrationReviewBody">
            _______________________________
            <div className="IndividualRegistrationReviewEventInformation">
               <div className="IndividualRegistrationReviewEventInformationHeading">
                  Event Information
               </div>
               <div className="IndividualRegistrationReviewEventInformationContent">
                  <div className="IndividualRegistrationReviewCampName">
                     Retreat Selected: {week.camp_name}
                  </div>
                  <div className="IndividualRegistrationReviewStartDate">
                     Dates: {DateHelpers.getEventDateRange(week.begin_date, week.end_date)}
                  </div>
                  <div className="IndividualRegistrationReviewGroupName">
                     Group: {groupName}
                  </div>
                  {config.hasRoomTypeChoice &&
                     <div className="IndividualRegistrationReviewRoomType">
                        Room Type: {roomType === 'deluxe' ? 'Deluxe' : 'Standard'}
                     </div>
                  }
                  {config.craftFair &&
                     <div className="IndividualRegistrationReviewCraftFair">
                        Craft Fair Table: {craftFairTable ? 'Yes' : 'No'}
                     </div>
                  }
                  {perSonFee > 0 && sonLines.length === 0 &&
                     <div className="IndividualRegistrationReviewSons">
                        Sons: None
                     </div>
                  }
                  {sonLines.map((son) => (
                     <div className="IndividualRegistrationReviewSons" key={son.position}>
                        {son.firstName} {son.lastName}:&nbsp;&nbsp;${son.price.toFixed(2)}
                     </div>
                  ))}
               </div>
            </div>
            _______________________________
            <div className="IndividualRegistrationReviewCostBreakdown">
               <div className="IndividualRegistrationReviewCostBreakdownHeading">
                  Cost Breakdown
               </div>
               <div className="IndividualRegistrationReviewCostBreakdownContent">
                  <div className="IndividualRegistrationReviewRegistrationFee">
                     Registration Fee{attendeeCount > 1 && ` (${attendeeCount} × $${registrationFee})`}:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${registrationFeeDue.toFixed(2)}
                  </div>
                  <div className="IndividualRegistrationReviewCampFee">
                     Retreat Fee{attendeeCount > 1 && ` (${attendeeCount} × $${campFee})`}:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${campFeeDue.toFixed(2)}
                  </div>
                  {roomSurcharge > 0 &&
                     <div className="IndividualRegistrationReviewRoomUpgrade">
                        Room Upgrade (Deluxe):&nbsp;+${roomSurcharge.toFixed(2)}
                     </div>
                  }
                  {/* The sons are already counted into the two fee lines above, so this states who the
                      head count covers instead of adding their cost a second time. */}
                  {sonCount > 0 &&
                     <div className="IndividualRegistrationReviewSonFee">
                        Covers you + {sonCount} {sonCount === 1 ? 'son' : 'sons'} at ${perSonFee.toFixed(2)} each
                     </div>
                  }
                  <div className="IndividualRegistrationReviewTotalAmountDue">
                     Total Amount Due:&nbsp;${reviewPaymentDue}
                  </div>
               </div>
            </div>
            _______________________________
         </div>
         {addToCartError &&
            <div className="IndividualRegistrationReviewError">
               {addToCartError}
            </div>
         }
         <div className="IndividualRegistrationReviewButtons">
            <div className="IndividualRegistrationReviewPreviousButton">
               <button onClick={handlePrevious} disabled={addingToCart}>
                  Previous
               </button>
            </div>
            <div className="IndividualRegistrationReviewNextButton">
               <button onClick={handleNext} disabled={addingToCart}>
                  {addingToCart ? 'Adding…' : 'Add to Cart'}
               </button>
            </div>
         </div>
      </div>
   )
}

export default RetreatRegistrationReview;
